import React, { FC, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  useSaveNewGuestAddressMutation,
  useUpdateAddressMutation
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'
import { alertError, alertSuccess } from 'shared/js/alertUtils'
import AddressDetailsScreen from '../../../address_modal_base/screens/AddressDetailsScreen'
import { AddressFormData } from '../../../address_modal_base/types'
import { AccountAddressScreenProps } from '../AccountAddressModal'
import { t } from 'src/translations/src'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'

interface EditAndConfirmAddressProps extends AccountAddressScreenProps {
  onSuccessfulUpdate: () => void
  action: 'add' | 'edit'
}

export const EditAndConfirmAddress: FC<EditAndConfirmAddressProps> = ({
  address,
  onSuccessfulUpdate,
  closeModal,
  goToScreen,
  action: entryPoint
}) => {
  const [validationError, setValidationError] = useState<string>('')
  const [UpdateAddressMutation] = useUpdateAddressMutation()
  const [SaveNewGuestAddressMutation] = useSaveNewGuestAddressMutation()
  const { refetchCustomer } = useCustomer()
  const ooClient = useOOClient()

  const formMethods = useForm<AddressFormData>({
    defaultValues: {
      intermediateAddress2: address?.deliveryInfo?.address2 || '',
      intermediateDeliveryInstructions: address?.deliveryInfo?.notes || '',
      intermediateAddressLabel: address?.name || ''
    }
  })

  const onAddAddress = useCallback(async () => {
    if (!address) return

    const formValues = formMethods.getValues()

    try {
      const { data } = await SaveNewGuestAddressMutation({
        variables: {
          input: {
            address: {
              addressLines: [
                address.deliveryInfo?.address1 || '',
                formValues.intermediateAddress2 || ''
              ],
              administrativeArea:
                address.deliveryInfo?.state ||
                address.deliveryInfo?.administrativeArea ||
                '',
              locality: address.deliveryInfo?.city || '',
              postalCode: address.deliveryInfo?.zipCode || '',
              regionCode: address.deliveryInfo?.country || ''
            },
            coordinates: {
              latitude: address.deliveryInfo?.latitude,
              longitude: address.deliveryInfo?.longitude
            },
            notes: formValues.intermediateDeliveryInstructions || '',
            label: formValues.intermediateAddressLabel || ''
          }
        },
        client: ooClient
      })
      if (!data) {
        setValidationError(t('address-add-screen.save.error'))
      } else if (
        data.saveNewGuestAddress.__typename === 'GuestAddressLimitExceededError'
      ) {
        setValidationError(t('address-add-screen.save.error-limit-exceeded'))
      } else if (data.saveNewGuestAddress.__typename === 'UnexpectedError') {
        setValidationError(t('address-add-screen.save.error'))
      } else if (
        data.saveNewGuestAddress.__typename === 'NewGuestAddressSuccess'
      ) {
        alertSuccess(t('address-add-screen.save.success'))
        refetchCustomer()
        onSuccessfulUpdate()
      }
    } catch (e) {
      alertError(t('address-screen.error'))
      reportError(e)
    }
  }, [
    SaveNewGuestAddressMutation,
    address,
    onSuccessfulUpdate,
    ooClient,
    formMethods,
    refetchCustomer
  ])

  const onEditAddress = useCallback(async () => {
    if (!address) return

    const formValues = formMethods.getValues()

    try {
      const { data } = await UpdateAddressMutation({
        variables: {
          input: {
            name: formValues.intermediateAddressLabel || '',
            deliveryInfo: {
              address1: address.deliveryInfo?.address1 || '',
              address2: formValues.intermediateAddress2 || '',
              city: address.deliveryInfo?.city || '',
              state: address.deliveryInfo?.state || '',
              zipCode: address.deliveryInfo?.zipCode || '',
              latitude: address.deliveryInfo?.latitude,
              longitude: address.deliveryInfo?.longitude,
              notes: formValues.intermediateDeliveryInstructions || '',
              administrativeArea:
                address.deliveryInfo?.administrativeArea || '',
              country: address.deliveryInfo?.country || ''
            },
            guid: address.guid
          }
        },
        client: ooClient
      })
      if (!data || data.updateAddress.__typename === 'UpdateAddressError') {
        setValidationError(t('address-edit-screen.update.error'))
      } else if (data.updateAddress.__typename === 'UpdateAddressResponse') {
        alertSuccess(t('address-edit-screen.update.success'))
        onSuccessfulUpdate()
      }
    } catch (e) {
      alertError(t('address-screen.error'))
      reportError(e)
    }
  }, [
    UpdateAddressMutation,
    address,
    onSuccessfulUpdate,
    ooClient,
    formMethods
  ])

  return (
    <AddressDetailsScreen
      closeModal={closeModal}
      backOrEditButtonHandler={() => {
        goToScreen('address-edit', address)
      }}
      confirmHandler={entryPoint === 'add' ? onAddAddress : onEditAddress}
      address={address}
      formMethods={formMethods}
      allowBackNav={false}
      errorMsg={validationError}
      entryPoint={entryPoint}
    />
  )
}
